import type { CSSProperties } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, VIEW } from "../bits";
import { EVENT } from "./event";
import "./Marquee.css";

/* Variant A: a vintage cinema marquee. A warm-black sign ringed with chasing
   bulbs and topped with a neon "Halloween movie night", the headline hung on a
   backlit letterboard, and the poster framed like a one-sheet in a lightbox. */

// Bulb positions around the sign, as fractions along each edge.
const ACROSS = 22, DOWN = 9;
const BULBS: { x: number; y: number }[] = [
  ...Array.from({ length: ACROSS + 1 }, (_, i) => ({ x: i / ACROSS, y: 0 })),
  ...Array.from({ length: DOWN - 1 }, (_, i) => ({ x: 1, y: (i + 1) / DOWN })),
  ...Array.from({ length: ACROSS + 1 }, (_, i) => ({ x: 1 - i / ACROSS, y: 1 })),
  ...Array.from({ length: DOWN - 1 }, (_, i) => ({ x: 0, y: 1 - (i + 1) / DOWN })),
];

/** Small deterministic wobble so the letters look hand-hung, not typeset. */
const wobble = (i: number) => ({ "--dy": `${((i * 7) % 5) - 2}px`, "--r": `${(((i * 11) % 7) - 3) * 0.4}deg` }) as CSSProperties;

function Letters({ text, className, offset = 0 }: { text: string; className: string; offset?: number }) {
  return <span className={`evm-row ${className}`} aria-hidden="true">
    {text.split("").map((c, i) => c === " "
      ? <span key={i} className="evm-gap" />
      : <motion.span key={i} className="evm-letter" style={wobble(i + offset)}
          initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW}
          transition={{ delay: 0.35 + (i + offset) * 0.025, duration: 0.35, ease: EASE }}>{c}</motion.span>)}
  </span>;
}

export function EventMarquee() {
  const [watch, craft] = EVENT.headline;
  const when = `${EVENT.weekday}, ${EVENT.month} ${EVENT.day} · ${EVENT.time}`;
  return <motion.div className="evm-wrap" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0, rotate: -1 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
    <Link to={EVENT.href} className="evm"
      aria-label={`${EVENT.kicker}: ${watch} ${EVENT.film} ${craft} ${EVENT.title}, ${when}, ${EVENT.venue}. Get tickets, ${EVENT.price}.`}>
      <span className="evm-bulbs" aria-hidden="true">
        {BULBS.map((b, i) => <i key={i} style={{ "--x": b.x, "--y": b.y, "--i": i } as CSSProperties} />)}
      </span>

      <span className="evm-neon" aria-hidden="true">{EVENT.kicker}</span>

      <span className="evm-face">
        <span className="evm-sheet">
          <img src={EVENT.poster} alt={EVENT.posterAlt} width={1080} height={1350} loading="lazy" />
          <span className="evm-sheet-tag">Now showing</span>
        </span>

        <span className="evm-panel" aria-hidden="true">
          <Letters text={watch.toUpperCase()} className="evm-small" />
          <motion.span className="evm-film" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={VIEW} transition={{ delay: 0.55, duration: 0.5 }}>{EVENT.film}</motion.span>
          <Letters text={craft.toUpperCase()} className="evm-big" offset={6} />
          <Letters text={`${EVENT.weekday} ${EVENT.month} ${EVENT.day} · ${EVENT.time}`.toUpperCase()} className="evm-when" offset={22} />
        </span>
      </span>

      <span className="evm-foot">
        <span className="evm-venue"><b>{EVENT.title}</b>{EVENT.venue}</span>
        <span className="evm-cta">Get tickets · {EVENT.price} <ArrowRight size={16} aria-hidden="true" /></span>
      </span>
    </Link>
  </motion.div>;
}
